import { Injectable } from "@angular/core";
import {
  Router,
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from "@angular/router";
import { Observable } from "rxjs";
import { map, take } from "rxjs/operators";
import { AuthenticationService } from "../../services/auth.service";
import { RestrictionService } from "../../student-dashboard/account-holds/restriction.service";

@Injectable({
  providedIn: "root",
})
export class HoldRestrictionGuard implements CanActivate {
  // Map routes to module IDs for restriction checking
  private routeModuleMap: Record<string, string> = {
    // Applications related
    applications: "applications",
    "application-form": "applications",

    // Enrollment related
    "enrollment-table": "enrollment-table",
    "enrollment-form": "enrollment-form",

    // Forms/Checklist related
    checklist: "forms",
    "checklist-form": "checklist-form",
    "student-required-forms": "student-required-forms",

    // Payment related
    "payment-summary": "payment-history",
    "payment-schedule": "payment-schedule",
    "student-resource": "student-resource",

    // Financial Aid related
    "financial-aid": "financial-aid",
    "financial-aid-history": "financial-aid-history",
    "financial-aid-form": "financial-aid-form",

    // Messages
    "message-panel": "messages",

    // Announcements
    announcements: "announcements",

    // PROFILE / PERSONAL INFO - ADD THESE
    profile: "personal-info",
    settings: "personal-info",
    "personal-info": "personal-info",

    // GRADUATION - ADD THESE
    graduation: "graduation",
    "graduation-application": "graduation",

    // TRANSCRIPTS - ADD THESE
    transcripts: "transcripts",
    "request-transcript": "transcripts",

    // LIBRARY - ADD THESE
    library: "library",

    // HOUSING - ADD THESE
    housing: "housing",
    "housing-application": "housing",

    // CLINICAL - ADD THESE
    "clinical-rotations": "clinical-rotations",

    // EXAM SCHEDULING - ADD THESE
    "exam-scheduling": "exam-scheduling",

    // REGISTRATION - ADD THESE
    registration: "registration",
    "registration-form": "registration",

    // INTERNATIONAL TRAVEL - ADD THESE
    "international-travel": "international-travel",

    //admissions
    admissions: "admissions",
    "admissions-form": "admissions",
    "admissions-table": "admissions",
  };

  constructor(
    private router: Router,
    private authService: AuthenticationService,
    private restrictionService: RestrictionService,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean> {
    // Get the module ID from route data or map
    const moduleId =
      route.data["moduleId"] ||
      this.routeModuleMap[route.routeConfig?.path || ""];

    console.log("HoldRestrictionGuard checking:", {
      path: route.routeConfig?.path,
      moduleId: moduleId,
      url: state.url,
      userType: this.authService.currentUser()?.userType,
    });

    // If no module ID specified, allow access
    if (!moduleId) {
      console.log("No moduleId, allowing access");
      return new Observable((observer) => observer.next(true));
    }

    // Check if the current user is a student
    const user = this.authService.currentUser();
    if (user?.userType !== 1) {
      console.log("Not a student, allowing access");
      return new Observable((observer) => observer.next(true));
    }

    // Check if the module is restricted
    return this.restrictionService.isModuleRestricted(moduleId).pipe(
      take(1),
      map((isRestricted) => {
        console.log(`Module ${moduleId} restricted: ${isRestricted}`);

        if (isRestricted) {
          // Get restriction details for better messaging
          this.restrictionService
            .getModuleRestriction(moduleId)
            .subscribe((module) => {
              // Store the attempted URL
              sessionStorage.setItem("redirectAfterResolution", state.url);

              console.log(
                `🚫 Access denied to ${moduleId}, redirecting to account-holds`,
              );

              // Redirect to account holds page with context
              this.router.navigate(["/student-dashboard/account-holds"], {
                queryParams: {
                  restricted: moduleId,
                  message:
                    module?.message || "Access restricted due to active holds",
                  returnUrl: state.url,
                },
              });
            });
          return false;
        }

        console.log(`Access granted to ${moduleId}`);
        return true;
      }),
    );
  }
}
